/**
 * useAtHoldingCap · the ONE "is this rep at the holding cap?" selector (dispatch L4), shared by the
 * live board, the backlog table and the lead drawer's pool notice, so every Claim button agrees.
 *
 * Reads the last pulse and dispatch policy any surface has already loaded — it never fetches. With
 * nothing loaded yet (a drawer opened from the Pipeline, say) it answers "not at cap"; the server
 * still refuses with 422 HOLDING_CAP_REACHED, which poolErrors explains.
 */
import { useSyncExternalStore } from 'react'
import { AT_CAP_REASON, isAtHoldingCap } from '../poolDispatch'
import { DEFAULT_DISPATCH_POLICY, type DispatchPolicyApi } from '../types'
import { useLastPulse } from './useHotLeadBadge'

let policy: DispatchPolicyApi | null = null
const listeners = new Set<() => void>()

/** Called by `useDispatchPolicy` whenever a policy arrives. */
export function publishDispatchPolicy(next: DispatchPolicyApi | null): void {
  if (next === policy) return
  policy = next
  for (const l of listeners) l()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

const getSnapshot = () => policy

export function useAtHoldingCap(): { atCap: boolean; reason: string | undefined } {
  const pulse = useLastPulse()
  const p = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
  const atCap = isAtHoldingCap(pulse, p ?? DEFAULT_DISPATCH_POLICY)
  return { atCap, reason: atCap ? AT_CAP_REASON : undefined }
}
